// src/components/NearbyFoodsPreview.js
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../config";

export default function NearbyFoodsPreview() {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const askAndFetch = useCallback(async () => {
    let coords;
    try {
      setLoading(true);
      setErr("");

      coords = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error("geolocation-unsupported"));
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos.coords),
          (e) => reject(e),
          { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
        );
      });

      const body = {
        lat: coords.latitude,
        lng: coords.longitude,
        radius: 6000,
        max: 6,
      };

      const r = await fetch(apiUrl("/api/picks/nearby"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!r.ok) throw new Error(`HTTP_${r.status}`);

      const j = await r.json();
      const excluded = ["mcdonald", "jack in the box", "burger king", "wendy", "kfc", "taco bell", "popeyes", "chick-fil-a", "five guys", "sonic", "carl's jr", "domino", "pizza hut", "dairy queen", "in-n-out"];
      const unhealthyTerms = /burger|fried|pizza|donut|wings|hot dog/;
      const list = Array.isArray(j.results) ? j.results : [];
      setItems(list.filter((place) => {
        const name = (place.name || "").toLowerCase();
        return !excluded.some((chain) => name.includes(chain)) && !unhealthyTerms.test(name);
      }).slice(0, 5));
    } catch (e) {
      const message =
        e?.message === "geolocation-unsupported"
          ? t("location_not_supported")
          : t("enable_location_food");
      setErr(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let alive = true;

    askAndFetch();

    const onFocus = () => alive && askAndFetch();
    window.addEventListener("focus", onFocus);

    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [askAndFetch]);

  return (
    <div
      className="home-card"
      style={{
        background: "#141414",
        border: "1px solid #0a3d4f",
        borderRadius: 12,
        padding: "14px 16px",
        minHeight: 180,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h4 style={{ margin: 0, color: "#00ffcc" }}>{t("healthy_foods_near_you")}</h4>
        <Link to="/healthy-foods" style={{ color: "#79e5ff", textDecoration: "none" }}>
          {t("more")} →
        </Link>
      </div>

      {loading ? (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("finding_spots")}</div>
      ) : err ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: "tomato", marginBottom: 8 }}>{err}</div>
          <button
            onClick={askAndFetch}
            type="button"
            style={{
              background: "transparent",
              border: "1px solid #00bfff",
              color: "#00bfff",
              borderRadius: 8,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            {t("enable_location_retry")}
          </button>
        </div>
      ) : items.length ? (
        <ul style={{ listStyle: "none", margin: 10, padding: 0 }}>
          {items.map((p) => (
            <li
              key={p.placeId}
              style={{ padding: "8px 0", borderBottom: "1px solid #1f2a30" }}
              title={p.address}
            >
              <div style={{ fontWeight: 700 }}>{p.name}</div>
              <small style={{ opacity: 0.8, display: "block" }}>
                ⭐ {p.rating ?? "N/A"} · {p.address}
              </small>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("no_results")}</div>
      )}
    </div>
  );
}
